<template>
  <main class="content">
    <section class="main-section">
      <article class="main-section__content">
        <div class="primary-color sign-up-title" lang="hr">
          {{ (isAdminLocalization ? adminText('adminAll.hey_welcome_back_04a9d5', 'Hey, welcome back!') : $t('loginHeader', 'Hey, welcome back!')) }}
        </div>

        <google-login style="width: 100%; margin: 0" />
        <facebook-login style="width: 100%; margin-top: 16px" />

        <div class="email-login-text-container">
          <div class="spacer" />
          <div class="separator-text">{{ adminText('adminAll.login_with_mail_289b1f', 'Login with mail') }}</div>
          <div class="spacer" />
        </div>
        <v-form
          style="width: 100%"
          @submit.prevent="submitForm"
          @keydown.enter.native="submitForm()"
        >
          <login-form class="inputs" :vs="$v" :initial-values="{}" @values="form = $event" />
          <v-flex class="button-container">
            <main-button
              class="forgot-password"
              flat
              type="button"
              :text="(isAdminLocalization ? adminText('adminAll.forgot_password_4c29f7', 'Forgot password?') : $t('forgotPasswordLogin', 'Forgot password?'))"
              @click.prevent="goTo(routes.shared.forgotPassword)"
            />
            <main-button type="submit" :text="(isAdminLocalization ? adminText('adminAll.log_in_f7c400', 'Log in') : $t('logIn', 'Log in'))" depressed />
          </v-flex>
        </v-form>
        <div class="no-account-text-container">
          <div class="spacer" />
          <div class="separator-text">{{ adminText('adminAll.don_t_have_an_account_f838dc', 'Don\'t have an account?') }}</div>
          <div class="spacer" />
        </div>
        <div class="button-holder">
          <main-button
            :text="(isAdminLocalization ? adminText('adminAll.sign_up_0b8149', 'Sign up') : $t('signUp', 'Sign up'))"
            depressed
            @click="goTo(routes.shared.signUp, { redirectUrl })"
          />
        </div>
      </article>
    </section>
    <simple-snackbar class="loginSnackbar" v-bind.sync="snackbar" :message-prop="adminAllMessage(snackbar.messageProp)" />
  </main>
</template>

<script>
import AdminAllMessages from '@/components/admin/AdminAllMessages'
import AdminContext from '@/admin-i18n/context'
import LoginForm from './login-form.vue'
import MainButton from './../shared/main-button'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar'
import { email, required } from 'vuelidate/lib/validators'
import { LOGIN } from '@/graphql/auth.graphql'
import {
  GET_DEFAULT_ORGANIZATION,
  GET_TICKET_SELLER_ORGANIZATION,
} from '../../graphql/user.graphql'
import { submitGuard } from '@/utils'
import { OrganizationType, UserType } from '@/types'
import GoogleLogin from '@/components/user/GoogleLogin.vue'
import { mapActions, mapState } from 'vuex'
import FacebookLogin from './FacebookLogin.vue'
export default {
  mixins: [AdminContext, AdminAllMessages],
  components: {
    GoogleLogin,
    LoginForm,
    MainButton,
    SimpleSnackbar,
    FacebookLogin,
  },
  props: {
    redirectUrl: {
      type: String,
      default: null,
    },
  },
  validations() {
    return {
      form: {
        email: { required, email },
        password: { required },
      },
    }
  },
  data: () => ({
    snackbar: {},
    form: {},
    OrganizationType,
    UserType,
    isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
  }),
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
  },
  methods: {
    ...mapActions({
      fetchUser: 'auth/fetchUser',
    }),
    async submitForm() {
      await submitGuard(this, this.loginAndRedirect)
    },
    async loginAndRedirect() {
      const { data, errors } = await this.$apollo.mutate({
        mutation: LOGIN,
        variables: this.form,
      })
      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: errors[0].message,
          color: 'error',
        }
      } else {
        await this.$store.dispatch('auth/fetchUser')
        await this.handleUserRedirect()
      }
    },
    async handleUserRedirect() {
      const ticketSeller = this.$route.query.ticketSeller
      if (this.user.type === 'SUPER_ADMIN' && typeof ticketSeller === 'string' && /^[0-9a-f-]{36}$/i.test(ticketSeller)) {
        await this.$router.push({ path: '/admin-pages/refunds', query: { ticketSeller } })

        return
      }
      if (this.redirectUrl) {
        window.location.replace(this.redirectUrl)
      } else {
        const me = this.user
        if (me.type === this.UserType.TICKET_SELLER) {
          const {
            data: { getTicketSellerOrganization },
          } = await this.$apollo.query({
            query: GET_TICKET_SELLER_ORGANIZATION,
          })
          if (getTicketSellerOrganization.type === this.OrganizationType.THEATER) {
            window.location.href = process.env.VUE_APP_TICKET_SELLER_URL
          } else {
            await this.goTo(this.routes.main.ticketSellerSidebar)
          }
        } else if (me.type === this.UserType.TICKET_SELLER_ADMIN) {
          await this.goTo(this.routes.ticketSellerAdmin.reportsAdmin)
        } else {
          const {
            data: { getDefaultOrganization: defaultOrganization },
          } = await this.$apollo.query({
            query: GET_DEFAULT_ORGANIZATION,
          })
          if (defaultOrganization) {
            const eventScopedRoles = ['EVENT_ADMIN', 'EVENT_VIEWER']
            const isEventScoped = eventScopedRoles.includes(defaultOrganization.organizationRole)
            if (isEventScoped) {
              if (defaultOrganization.type === this.OrganizationType.THEATER) {
                this.goTo(this.routes.theaterOrganization.theaterOrganizationWorkingEvents, {
                  organizationSlug: defaultOrganization.slug,
                })
              } else if (defaultOrganization.type === this.OrganizationType.SPORT_ORGANIZATION) {
                this.goTo(this.routes.sportOrganization.sportOrganizationWorkingEvents, {
                  organizationSlug: defaultOrganization.slug,
                })
              } else {
                this.goTo(this.routes.organization.organizationWorkingEvents, {
                  organizationSlug: defaultOrganization.slug,
                })
              }
            } else if (defaultOrganization.type === this.OrganizationType.THEATER) {
              this.goTo(this.routes.main.theaterOrganizationSidebar, {
                organizationSlug: defaultOrganization.slug,
              })
            } else if (defaultOrganization.type === this.OrganizationType.SPORT_ORGANIZATION) {
              this.goTo(this.routes.main.sportOrganizationSidebar, {
                organizationSlug: defaultOrganization.slug,
              })
            } else {
              this.goTo(this.routes.main.organizationSidebar, {
                organizationSlug: defaultOrganization.slug,
              })
            }
          } else {
            await this.goTo(this.routes.organizer.organizations)
          }
        }
      }
    },
  },
}
</script>

<style scoped>
.mt-80 {
  margin-top: 80px;
}

.sign-up-title {
  font-family: Raleway, sans-serif;
  font-style: normal;
  font-weight: 800;
  font-size: 72px;
  line-height: 120%;
  letter-spacing: -0.03em;
  align-self: center;
  margin: 24px 0 64px;
  text-align: center;
}

.button-container {
  display: flex;
  max-width: 520px;
  justify-content: space-between;
  margin: 0 auto;
}

.forgot-password {
  font-size: 16px;
  text-transform: none;
  padding-right: 0;
  margin-left: -5px !important;
}

.email-login-text-container {
  display: flex;
  align-items: center;
  max-width: 500px;
  align-self: center;
  width: 100%;
  margin-top: 3rem;
}

@media (max-width: 800px) {
  .forgot-password {
    font-size: 16px;
  }
}

.content {
  display: grid;
  grid-auto-rows: auto;
  gap: 13.125rem;
  margin-bottom: 13.125rem;
}

.main-section {
  max-width: 1216px;
  padding: 0 2.625rem;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 662px 1fr;
  gap: 2.625rem;
  align-items: center;
}

.main-section__content {
  margin: 2.625rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

@media screen and (max-width: 680px) {
  .sign-up-title {
    font-size: 3.938rem;
  }

  .main-section {
    grid-template-columns: 1fr;
    margin: 0;
    padding: 0;
  }

  .main-section {
    grid-template-columns: 1fr;
  }

  .forgot-password {
    margin-left: -15px !important;
  }
}

.inputs {
  max-width: 500px;
  margin: 36px auto 0;
}

@media screen and (max-width: 420px) {
  .button-container {
    display: flex;
    flex-direction: column-reverse;
    margin: 0 auto;
  }

  .forgot-password {
    font-size: 14px;
    text-transform: none;
    padding-right: 0;
  }

  .sign-up-title {
    word-break: break-word;
    -webkit-hyphens: auto;
    -moz-hyphens: auto;
    -ms-hyphens: auto;
    hyphens: auto;
  }
}

.no-account-text-container {
  display: flex;
  align-items: center;
  max-width: 500px;
  align-self: center;
  width: 100%;
  margin-top: 3rem;
}

.spacer {
  flex: 1;
  border: 0.5px solid rgba(151, 151, 151, 0.5);
}

.separator-text {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 150%;
  text-align: center;
  color: #333333;
  margin-right: 1rem;
  margin-left: 1rem;
}

.button-holder {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-top: 1.5rem;
}

@media screen and (max-width: 800px) {
  .loginSnackbar {
    font-size: 20px !important;
  }
}
</style>
